import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Cpu, Loader2, CheckCircle2, AlertCircle, RefreshCw,
  Video as VideoIcon, ImageIcon, Inbox,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Two poll cadences: fast while anything is running, slow when idle.
const FAST_POLL_MS = 3000;
const IDLE_POLL_MS = 30000;

// Human-friendly labels for each job kind stored in `analysis_jobs`.
const KIND_META = {
  'rescrape-ad': {
    Icon: RefreshCw,
    tint: 'bg-sky-100 text-sky-700',
    label: 'Refresh ad',
  },
  'analyze-video': {
    Icon: VideoIcon,
    tint: 'bg-violet-100 text-violet-700',
    label: 'Analyze video',
  },
};

const STATUS_META = {
  running:  { Icon: Loader2, tint: 'text-amber-500', label: 'Running', spin: true },
  success:  { Icon: CheckCircle2, tint: 'text-emerald-600', label: 'Done' },
  error:    { Icon: AlertCircle, tint: 'text-red-500', label: 'Failed' },
};

// "just now", "3m ago", "2h ago"
const relTime = (iso) => {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return null; }
};

const shortId = (v) => (v ? String(v).slice(0, 8) : '');

export default function JobsFab() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(0);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const timer = useRef(null);

  const fetchJobs = async () => {
    try {
      const r = await fetch(`${API_URL}/api/jobs?limit=25`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const text = await r.text();
      let j;
      try { j = JSON.parse(text); } catch { throw new Error('non-JSON'); }
      if (!mounted.current) return;
      setItems(j.items || []);
      setRunning(j.running_count || 0);
      setError(null);
    } catch (e) {
      if (mounted.current) setError(e.message || 'Could not load jobs');
    }
  };

  // Adaptive polling: fast when a job is running, slow otherwise.
  useEffect(() => {
    mounted.current = true;
    fetchJobs();
    const schedule = () => {
      if (!mounted.current) return;
      clearTimeout(timer.current);
      const ms = running > 0 ? FAST_POLL_MS : IDLE_POLL_MS;
      timer.current = setTimeout(async () => {
        await fetchJobs();
        schedule();
      }, ms);
    };
    schedule();
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, [running]);

  // Refresh on popover open
  useEffect(() => {
    if (open) fetchJobs();
  }, [open]);

  const hasErrors = items.some(i => i.status === 'error');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="jobs-fab fixed bottom-40 right-6 z-40"
          data-testid="jobs-fab-btn"
          aria-label="Background jobs"
        >
          <div className={`relative w-12 h-12 rounded-full text-white shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center ${
            running > 0
              ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/30'
              : hasErrors
                ? 'bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/30'
                : 'bg-gradient-to-br from-slate-700 to-slate-800 shadow-slate-800/30'
          }`}>
            <Cpu className={`h-5 w-5 ${running > 0 ? 'animate-pulse' : ''}`} />
            {running > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-white text-amber-700 text-[11px] font-bold flex items-center justify-center shadow-md"
                data-testid="jobs-fab-badge"
              >
                {running}
              </span>
            )}
            {running > 0 && (
              <span className="absolute inset-0 rounded-full ring-2 ring-amber-300/60 animate-ping" />
            )}
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="left"
        className="w-96 p-0 rounded-xl overflow-hidden shadow-2xl border-slate-200"
        data-testid="jobs-fab-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
          <div>
            <h3 className="text-sm font-bold text-slate-900 tracking-tight">Background jobs</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {running > 0
                ? `${running} running · auto-refreshing every ${FAST_POLL_MS / 1000}s`
                : items.length > 0
                  ? `${items.length} recent · all clear`
                  : 'No jobs yet'}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchJobs}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="jobs-fab-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {error && (
            <div className="p-4 text-xs text-red-600 bg-red-50 border-b border-red-100">
              {error}
            </div>
          )}
          {items.length === 0 && !error ? (
            <div className="py-10 flex flex-col items-center gap-2 text-slate-400">
              <Inbox className="h-8 w-8" />
              <p className="text-xs">Nothing running or recent.</p>
              <p className="text-[11px] text-slate-400">Trigger a rescrape or video analysis and it&apos;ll show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(j => {
                const kind = KIND_META[j.kind] || { Icon: Cpu, tint: 'bg-slate-100 text-slate-600', label: j.kind || 'Job' };
                const status = STATUS_META[j.status] || STATUS_META.running;
                const StatusIcon = status.Icon;
                const KindIcon = kind.Icon;
                const createdAgo = relTime(j.created_at);
                const finishedAgo = relTime(j.finished_at);
                return (
                  <li key={j.job_id} className="px-4 py-3 hover:bg-slate-50/70 transition-colors" data-testid={`jobs-fab-item-${j.job_id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${kind.tint}`}>
                        <KindIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-semibold text-slate-900 truncate">{kind.label}</p>
                          <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${status.tint} ${status.spin ? 'animate-spin' : ''}`} />
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${status.tint}`}>{status.label}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate">
                          {j.filename && <span className="truncate">{j.filename}</span>}
                          {j.ad_id && <span className="font-mono">ad · {shortId(j.ad_id)}</span>}
                          {!j.filename && !j.ad_id && <span className="font-mono text-slate-400">{shortId(j.job_id)}</span>}
                        </p>
                        {j.status === 'error' && j.error && (
                          <p className="text-[11px] text-red-600 mt-1 leading-snug line-clamp-2" title={j.error}>{j.error}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">
                          {j.status === 'running' && createdAgo && <>Started {createdAgo}</>}
                          {j.status !== 'running' && finishedAgo && <>Finished {finishedAgo}</>}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
